
import {  useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import type { IMenu } from '@/interfaces/IMenu'
import useDarkMode from '@/hooks/useDarkMode'
import ModalSearch from 'components/ModalSearch'
import { Icon } from '@/assets/icons'
import {
  IconSearch,
  IconMoon,
  IconSun,
  IconBrandYoutube,
  IconX,
} from '@tabler/icons-react'

const NavbarAlt = () => {
  const modalSearchRef = useRef<{ openModal: () => void }>(null)
  const { theme, setTheme } = useDarkMode()
  const [darkMode, setDarkMode] = useState(theme === 'dark')
  const [menuOpen, setMenuOpen] = useState(false) // mobile/tablet slide-over

  const toggleDarkMode = (checked: boolean) => {
    setDarkMode(checked)
    setTheme(checked ? 'dark' : 'light')
  }

  // MENUS
  const moviesMenu: IMenu = {
    type: 'movie',
    label: 'Movies',
    href: '/movie/blue-fox-entertainment',
    menus: [
      { href: '/movie/domestic', label: 'Domestic' },
      { href: '/movie/international', label: 'International' },
      { href: '/movie/redhound-entertainment', label: 'Red Hound Entertainment' },
      { href: '/movie/blue-fox-canada', label: 'Blue Fox Canada' },
    ],
  }
  const financingMenu: IMenu = {
    type: 'financing',
    label: 'Financing',
    href: 'https://bluefoxfinancing.com',
    menus: [],
  }
  const canada: IMenu = {
    type: 'canada',
    label: 'Canada',
    href: 'https://bluefoxentertainment.ca',
    menus: [],
  }
  const international: IMenu = {
    type: 'international',
    label: 'International',
    href: '/festivals',
    menus: [],
  }
  const home: IMenu = { type: 'home', label: 'Home', href: '/', menus: [] }
  const shop: IMenu = { type: 'shop', label: 'Shop', href: 'https://unobstructedview.com', menus: [] }

  const YT_URL = 'https://www.youtube.com/channel/UCmHYPCM_h8Tw9JkI3UnrCvA'

  // turn desktop links on only at lg+ to “convert to mobile sooner”
  const desktopLinkCls = ({ isActive }: { isActive: boolean }) =>
    `px-2 py-1.5 rounded-md text-sm font-medium transition
     ${isActive
      ? 'text-blue-700 dark:text-blue-300 bg-slate-900/5 dark:bg-white/10'
      : 'text-slate-700 dark:text-slate-200 hover:bg-slate-900/5 dark:hover:bg-white/10'
    }`

  return (
    <header className="fixed top-1 inset-x-0 z-[100]">
      <div className="relative mx-auto max-w-7xl px-3 sm:px-4 mt-3">
        <div
          className="
            h-14 lg:h-16
            rounded-2xl border border-slate-900/10 dark:border-white/10
            bg-white/60 dark:bg-slate-900/60
            backdrop-blur-xl shadow-sm
            flex items-center justify-between gap-2 px-3 sm:px-4
          "
        >
          {/* Left: logo + desktop links (lg+) */}
          <div className="flex items-center gap-3 lg:gap-6">
            <NavLink to="/" className="flex items-center gap-2 lg:gap-3 mr-1">
              <img src={Icon} alt="Blue Fox" className="w-8 h-8 lg:w-11 lg:h-11 rounded-lg" />
              <span className="leading-tight">
                <span className="text-sm md:text-base lg:text-xl font-light bg-gradient-to-r from-blue-500 to-blue-700 bg-clip-text text-transparent">
                  BLUE FOX
                </span>
                <span className="ml-1 text-sm md:text-base lg:text-xl font-light bg-gradient-to-r from-blue-500 to-blue-700 bg-clip-text text-transparent">
                  ENTERTAINMENT
                </span>
              </span>
            </NavLink>

            {/* Desktop links appear only at lg and above */}
            <nav className="hidden lg:flex items-center gap-5">
              <NavLink to={moviesMenu.href!} className={desktopLinkCls}>
                {moviesMenu.label}
              </NavLink>
              <NavLink to={international.href!} className={desktopLinkCls}>
                {international.label}
              </NavLink>
              <a
                href={canada.href!}
                target="_blank"
                rel="noreferrer"
                className="px-2 py-1.5 rounded-md text-sm font-medium transition text-slate-700 dark:text-slate-200 hover:bg-slate-900/5 dark:hover:bg-white/10"
              >
                {canada.label}
              </a>
              <a
                href={financingMenu.href!}
                target="_blank"
                rel="noreferrer"
                className="px-2 py-1.5 rounded-md text-sm font-medium transition text-slate-700 dark:text-slate-200 hover:bg-slate-900/5 dark:hover:bg-white/10"
              >
                {financingMenu.label}
              </a>
              <a
                href={shop.href!}
                target="_blank"
                rel="noreferrer"
                className="px-2 py-1.5 rounded-md text-sm font-medium transition text-slate-700 dark:text-slate-200 hover:bg-slate-900/5 dark:hover:bg-white/10"
              >
                {shop.label}
              </a>
              <NavLink to={home.href!} className={desktopLinkCls}>
                {home.label}
              </NavLink>
            </nav>
          </div>

          {/* Right: actions (lg+), hamburger (<lg) */}
          {/* Right: actions (YouTube + Search always visible), theme + extras lg+, hamburger <lg */}
          <div className="flex items-center gap-2">
            {/* Always-visible (all breakpoints) */}
            <a
              href={YT_URL}
              target="_blank"
              rel="noreferrer"
              aria-label="YouTube"
              className="
                  inline-grid place-items-center h-9 w-9 rounded-lg
                  border-2 border-red-700 dark:border-b-red-600
                  bg-slate-900/5 dark:bg-white/10
                  text-slate-700 dark:text-slate-100
                  transition-colors duration-200
                  hover:bg-[#FF0000] hover:border-[#FF0000] hover:text-white
                  dark:hover:bg-[#FF0000] dark:hover:border-[#FF0000] dark:hover:text-white
                  active:bg-[#E50000] active:border-[#E50000]
                  dark:active:bg-[#CC0000] dark:active:border-[#CC0000]
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60
                  focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900
                "
            >
              <IconBrandYoutube size={20} />
            </a>

            <button
              onClick={() => modalSearchRef.current?.openModal()}
              className="inline-flex items-center gap-2 h-9 rounded-lg px-3 text-sm font-medium
               bg-slate-900/5 dark:bg-white/10 hover:bg-slate-900/10 dark:hover:bg-white/15
               text-slate-700 dark:text-slate-100 transition"
              aria-label="Search"
            >
              <IconSearch size={18} />
              {/* hide the label on small screens to keep it compact */}
              <span className="hidden md:inline text-xs opacity-70">Search</span>
            </button>

            {/* Large screens: extra actions (theme) */}
            <div className="hidden lg:flex">
              <button
                onClick={() => toggleDarkMode(!darkMode)}
                className="inline-grid place-items-center h-9 w-9 rounded-lg
                 bg-slate-900/5 dark:bg-white/10 hover:bg-slate-900/10 dark:hover:bg-white/15
                 text-slate-700 dark:text-slate-100 transition ml-2"
                aria-label="Toggle dark mode"
              >
                {darkMode ? <IconSun size={18} /> : <IconMoon size={18} />}
              </button>
            </div>

            {/* Small/medium: hamburger */}
            <button
              className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-lg
               bg-slate-900/5 dark:bg-white/10 hover:bg-slate-900/10 dark:hover:bg-white/15
               text-slate-700 dark:text-slate-100 transition"
              aria-label="Open menu"
              onClick={() => setMenuOpen(true)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M4 7h16v2H4zM4 11h16v2H4zM4 15h16v2H4z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Slide-over menu for <lg (mobile + tablet) */}
      {menuOpen && (
        <div className="lg:hidden fixed inset-0 z-[110]">
          {/* backdrop */}
          <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} aria-hidden />
          {/* panel */}
          <div
            className="
              absolute inset-x-0 top-0
              h-[85vh] rounded-b-2xl
              bg-white dark:bg-slate-900
              shadow-2xl border-b border-slate-900/10 dark:border-white/10
              p-4 space-y-3 overflow-y-auto
            "
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <img src={Icon} alt="Blue Fox" className="h-8 w-8 rounded-md" />
                <span className="text-base font-semibold text-slate-900 dark:text-slate-100">BFE</span>
              </div>
              <button
                className="h-9 w-9 rounded-lg grid place-items-center hover:bg-gray-100/50 dark:hover:bg-white/10"
                onClick={() => setMenuOpen(false)}
                aria-label="Close menu"
              >
                <IconX size={18} />
              </button>
            </div>

            {/* Quick actions */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setMenuOpen(false); modalSearchRef.current?.openModal() }}
                className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium
                           bg-slate-900/5 dark:bg-white/10 hover:bg-slate-900/10 dark:hover:bg-white/15
                           text-slate-700 dark:text-slate-100 transition"
              >
                <IconSearch size={18} /> Search
              </button>
              <button
                onClick={() => toggleDarkMode(!darkMode)}
                className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium
                           bg-slate-900/5 dark:bg-white/10 hover:bg-slate-900/10 dark:hover:bg-white/15
                           text-slate-700 dark:text-slate-100 transition"
              >
                {darkMode ? <IconSun size={18} /> : <IconMoon size={18} />}
                {darkMode ? 'Light' : 'Dark'}
              </button>
              <a
                href={YT_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium
                           bg-slate-900/5 dark:bg-white/10 hover:bg-slate-900/10 dark:hover:bg-white/15
                           text-slate-700 dark:text-slate-100 transition"
              >
                <IconBrandYoutube size={18} /> YouTube
              </a>
            </div>

            {/* Navigation - matches desktop order */}
            <nav className="space-y-2">
              <NavLink
                to={moviesMenu.href!}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-sm font-medium transition ${
                    isActive
                      ? 'bg-blue-600/10 text-blue-700 dark:text-blue-300'
                      : 'bg-slate-900/5 dark:bg-white/10 hover:bg-slate-900/10 dark:hover:bg-white/15 text-slate-800 dark:text-slate-100'
                  }`
                }
              >
                {moviesMenu.label}
              </NavLink>

              <NavLink
                to={international.href!}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-sm font-medium transition ${
                    isActive
                      ? 'bg-blue-600/10 text-blue-700 dark:text-blue-300'
                      : 'bg-slate-900/5 dark:bg-white/10 hover:bg-slate-900/10 dark:hover:bg-white/15 text-slate-800 dark:text-slate-100'
                  }`
                }
              >
                {international.label}
              </NavLink>

              <a
                href={canada.href!}
                target="_blank"
                rel="noreferrer"
                onClick={() => setMenuOpen(false)}
                className="block px-4 py-3 rounded-xl text-sm font-medium transition bg-slate-900/5 dark:bg-white/10 hover:bg-slate-900/10 dark:hover:bg-white/15 text-slate-800 dark:text-slate-100"
              >
                {canada.label}
              </a>

              <a
                href={financingMenu.href!}
                target="_blank"
                rel="noreferrer"
                onClick={() => setMenuOpen(false)}
                className="block px-4 py-3 rounded-xl text-sm font-medium transition bg-slate-900/5 dark:bg-white/10 hover:bg-slate-900/10 dark:hover:bg-white/15 text-slate-800 dark:text-slate-100"
              >
                {financingMenu.label}
              </a>

              <a
                href={shop.href!}
                target="_blank"
                rel="noreferrer"
                onClick={() => setMenuOpen(false)}
                className="block px-4 py-3 rounded-xl text-sm font-medium transition bg-slate-900/5 dark:bg-white/10 hover:bg-slate-900/10 dark:hover:bg-white/15 text-slate-800 dark:text-slate-100"
              >
                {shop.label}
              </a>

              <NavLink
                to={home.href!}
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `block px-4 py-3 rounded-xl text-sm font-medium transition ${
                    isActive
                      ? 'bg-blue-600/10 text-blue-700 dark:text-blue-300'
                      : 'bg-slate-900/5 dark:bg-white/10 hover:bg-slate-900/10 dark:hover:bg-white/15 text-slate-800 dark:text-slate-100'
                  }`
                }
              >
                {home.label}
              </NavLink>
            </nav>
          </div>
        </div>
      )}
      {/* Optional: keep your existing bottom dock if you like. With the earlier collapse (lg), you might not need it. */}

      <ModalSearch ref={modalSearchRef} />
    </header>
  )
}

export default NavbarAlt
